<template>
  <div>
    <h2 class="heading-1 relative">
      {{ i18n(translations.title) }}
    </h2>

    <div
      class="mt-18"
      :class="{
        'md:grid md:grid-cols-2 md:items-start md:gap-x-32': twoColumns,
      }"
    >
      <RevAccordionList
        v-for="(column, columnIndex) in faqColumns"
        :key="column[0].id"
        :has-external-borders="false"
        :class="{
          'border-t md:border-t-0 border-static-default-low': columnIndex > 0,
        }"
      >
        <RevAccordionItem v-for="item in column" :key="item.id">
          <template #title>
            {{ item.title }}
          </template>
          <template #body>
            <FormattedMessage
              class="body-1 whitespace-pre-line"
              :definition="item.body"
            >
              <template
                v-for="link in item.links"
                :key="link.slot"
                #[link.slot]
              >
                <RevLink class="body-1-bold" target="_blank" :to="link.href">
                  {{ link.label }}
                </RevLink>
              </template>
            </FormattedMessage>
          </template>
        </RevAccordionItem>
      </RevAccordionList>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useHead, useRouteLocationWithLocale } from '#imports'
import { computed } from 'vue'

import { useZendeskFaqBaseUrl } from '@backmarket/nuxt-layer-base/composables/useZendeskFaqBaseUrl'
import FormattedMessage from '@backmarket/nuxt-module-i18n/FormattedMessage.vue'
import type { I18nDefinition } from '@backmarket/nuxt-module-i18n/types/i18n'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { RevAccordionItem } from '@ds/components/AccordionItem'
import { RevAccordionList } from '@ds/components/AccordionList'
import { RevLink } from '@ds/components/Link'
import type { Location } from '@ds/types/Location'

import { CMS } from '~/scopes/cms/routes-names'
import { ROUTE_NAMES } from '~/scopes/dashboard/constants'
import { LEGAL_PAGE_NAME } from '~/scopes/legal/constants'
import { useRegisterShoppingAssistantStructuredData } from '~/scopes/shopping-assistant/composables/useShoppingAssistantStructuredData'
import { buildFaqSchema, prepareJsonLdScript } from '~/utils/seo/schema'

import translations from './SeoBlock.translations'

const props = defineProps<{ twoColumns?: boolean }>()

const { DASHBOARD } = ROUTE_NAMES

const i18n = useI18n()
const resolveLocalizedRoute = useRouteLocationWithLocale()
const { zendeskFaqBaseUrl } = useZendeskFaqBaseUrl()

const FAQ_LINKS = {
  LOGIN: resolveLocalizedRoute({ name: DASHBOARD.MY_ORDERS.SELF }),
  RETURN_REFUND: zendeskFaqBaseUrl.concat(
    '/articles/360014698640-Returns-and-refunds-instructions',
  ),
  WARRANTY: resolveLocalizedRoute({
    name: CMS.LEGAL_PAGE,
    params: {
      pageName: LEGAL_PAGE_NAME.WARRANTY,
    },
  }),
} as const

type FaqLinkSlot = 'login' | 'return' | 'exceptions' | 'refund'

interface FaqLink {
  slot: FaqLinkSlot
  label: string
  href: Location
}

interface FaqItem {
  id: string
  title: string
  body: I18nDefinition
  jsonld: I18nDefinition
  links: FaqLink[]
}

const faqItems: FaqItem[] = [
  {
    id: 'when-should-i-contact',
    title: i18n(translations.when_should_i_contact_title),
    body: translations.when_should_i_contact_body,
    jsonld: translations.when_should_i_contact_jsonld,
    links: [
      { slot: 'login', label: i18n(translations.login), href: FAQ_LINKS.LOGIN },
    ],
  },
  {
    id: 'how-do-i-cancel-an-order',
    title: i18n(translations.how_do_i_cancel_an_order_title),
    body: translations.how_do_i_cancel_an_order_body,
    jsonld: translations.how_do_i_cancel_an_order_jsonld,
    links: [
      { slot: 'login', label: i18n(translations.login), href: FAQ_LINKS.LOGIN },
      {
        slot: 'return',
        label: i18n(translations.returns),
        href: FAQ_LINKS.RETURN_REFUND,
      },
    ],
  },
  {
    id: 'how-can-i-ask-for-a-repair',
    title: i18n(translations.how_can_i_ask_for_a_repair_title),
    body: translations.how_can_i_ask_for_a_repair_body,
    jsonld: translations.how_can_i_ask_for_a_repair_jsonld,
    links: [
      { slot: 'login', label: i18n(translations.login), href: FAQ_LINKS.LOGIN },
      {
        slot: 'exceptions',
        label: i18n(translations.exceptions),
        href: FAQ_LINKS.WARRANTY,
      },
      {
        slot: 'return',
        label: i18n(translations.returns),
        href: FAQ_LINKS.RETURN_REFUND,
      },
    ],
  },
  {
    id: 'how-can-i-get-a-refund',
    title: i18n(translations.how_can_i_get_a_refund_title),
    body: translations.how_can_i_get_a_refund_body,
    jsonld: translations.how_can_i_get_a_refund_jsonld,
    links: [
      { slot: 'login', label: i18n(translations.login), href: FAQ_LINKS.LOGIN },
      {
        slot: 'refund',
        label: i18n(translations.refund),
        href: FAQ_LINKS.RETURN_REFUND,
      },
    ],
  },
]

const faqColumns = computed(() => {
  if (!props.twoColumns) return [faqItems]

  const midpoint = Math.ceil(faqItems.length / 2)

  return [faqItems.slice(0, midpoint), faqItems.slice(midpoint)]
})

const faqSchemaData = faqItems.map(({ title, jsonld, links }) => ({
  question: title,
  answer: i18n(
    jsonld,
    Object.fromEntries(links.map((link) => [link.slot, link.label])),
  ),
}))

const faqSchema = buildFaqSchema(faqSchemaData)

useHead(() => ({
  script: [prepareJsonLdScript(faqSchema)],
}))

useRegisterShoppingAssistantStructuredData(faqSchema)
</script>
